import { useCallback, useEffect, useMemo, useState } from "react";
import useAssetFilterType from "domains/assets/hooks/useAssetFilterType";
import { useAssetsByIds } from "domains/assets/hooks/useAssetsByIds";
import usePersistedState, {
  PersistedStateKey,
} from "domains/commons/hooks/usePersistedState";
import { formatTag } from "domains/commons/misc";
import {
  FILTER_IMAGE_TYPES_KEYS,
  FILTER_SKYBOX_TYPES_KEYS,
  FILTER_TEXTURE_TYPES_KEYS,
} from "domains/file-manager/constants/AssetFilter";
import { getImageThumbnail } from "domains/file-manager/interfaces";
import reduceAssetFilterToTypes from "domains/file-manager/utils/reduceAssetFilterToTypes";
import { getCustomAssetId } from "domains/inference/constants/explore";
import {
  ExploreSuggestArgs,
  ExploreTab,
} from "domains/inference/interfaces/Explore";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useSearchContext } from "domains/search/contexts/SearchProvider";
import { MeilisearchResult } from "domains/search/interfaces/Search";

export default function useExploreTabsStatus(
  type: "image" | "skybox" | "texture" | "video" | "3d",
  suggestArgs?: ExploreSuggestArgs
) {
  const { getAllIndexes, searchWithTokenRenewal } = useSearchContext();
  const { errorToast } = useScenarioToast();
  const [tags, setTags] = useState<string[] | undefined>();
  const [tabs, setTabs] = useState<ExploreTab[] | undefined>();
  const [persistedTabResultCounts, setPersistedTabResultCounts] =
    usePersistedState<{
      [key: string]: number;
    }>(PersistedStateKey.LAST_EXPLORE_TABS_COUNTS, { defaultValue: {} });
  const [tabResultCounts, setTabResultCounts] = useState<{
    [key: string]: number;
  }>({});

  const { allAssetsTypeArgs: imageTypesQueryArgs } = useAssetFilterType({
    assetType: type,
    excludeUpload: true,
    showVectorization: true,
    avoidPersistedState: true,
  });

  // Uncomment when CLOUD is ready (add style to meilisearch indexes)
  const isSuggestsAvailable =
    !!(suggestArgs?.model?.id /* || suggestArgs?.style*/);

  const publicIndex = useMemo(
    () => getAllIndexes("asset").public,
    [getAllIndexes]
  );

  const tabsStatus = useMemo(() => {
    const status: { [key: string]: boolean } = {};
    for (const tabId in tabResultCounts) {
      status[tabId] =
        tabResultCounts[tabId] === 0 ||
        tabResultCounts[tabId] === persistedTabResultCounts[tabId];
    }
    return status;
  }, [tabResultCounts, persistedTabResultCounts]);

  const tabsAssetIds = useMemo(
    () => (tabs ?? []).map((tab) => tab.assetId),
    [tabs]
  );
  const { assetsByIds: tabsAssetsByIds } = useAssetsByIds({
    assetIds: tabsAssetIds,
  });

  const tabsWithImg = useMemo(
    () =>
      tabs?.map((tab) => {
        const tabAsset = tabsAssetsByIds[tab.assetId];
        return {
          ...tab,
          imgUrl: tabAsset
            ? getImageThumbnail({
                asset: tabAsset.meta,
                width: 256,
              })
            : undefined,
        };
      }),
    [tabs, tabsAssetsByIds]
  );

  // ----------------------------------

  const markAsUpToDate = useCallback(
    (tabId: string) => {
      setPersistedTabResultCounts((persistedCounts) => ({
        ...persistedCounts,
        [tabId]: tabResultCounts[tabId],
      }));
    },
    [setPersistedTabResultCounts, tabResultCounts]
  );

  // ----------------------------------

  useEffect(() => {
    if (!publicIndex) return;
    if (!searchWithTokenRenewal) return;

    const search = async () => {
      const filterByType = reduceAssetFilterToTypes(
        (type === "image" && [...FILTER_IMAGE_TYPES_KEYS]) ||
          (type === "skybox" && [...FILTER_SKYBOX_TYPES_KEYS]) ||
          (type === "texture" && [...FILTER_TEXTURE_TYPES_KEYS]) || [
            ...FILTER_IMAGE_TYPES_KEYS,
          ]
      );
      const query = {
        indexName: publicIndex,
        params: {
          query: "",
          filters: `tags IS NOT EMPTY AND (${filterByType
            .map((type) => `metadata.type = ${type}`)
            .join(" OR ")})`,
          facets: ["tags"],
          hitsPerPage: 0,
        },
      };

      try {
        const response = await searchWithTokenRenewal([query]);
        if (!response) return;
        const results: MeilisearchResult[] = response.results;
        setTags(Object.keys(results[0]?.facets.tags ?? {}));
      } catch (error) {
        errorToast({
          title: "Error fetching explore results",
          description: "Please try again later",
        });
      }
    };
    void search();
  }, [errorToast, publicIndex, searchWithTokenRenewal, setTags, type]);

  useEffect(() => {
    if (!publicIndex) return;
    if (!searchWithTokenRenewal) return;
    if (!tags) return;

    const search = async () => {
      const typeFilter = imageTypesQueryArgs.types
        .map((type) => `metadata.type = "${type}"`)
        .join(" OR ");
      const queries = tags.map((tag) => ({
        indexName: `${publicIndex}:createdAtTS:desc`,
        params: {
          query: "",
          filters: `tags = "${tag}" AND (${typeFilter})`,
          // page: 0,
          hitsPerPage: 1,
          limit: 1,
        },
      }));

      try {
        const response = await searchWithTokenRenewal(queries);
        if (!response) return;
        const results: MeilisearchResult[] = response.results;
        setTabs(() =>
          results.reduce<ExploreTab[]>((memo, result, idx) => {
            const assetId = getCustomAssetId(tags[idx], result.hits[0]?.id);
            if (assetId) {
              memo.push({
                id: tags[idx],
                title: formatTag(tags[idx]),
                assetId,
                thumbnailUrl: `${process.env.NEXT_PUBLIC_CDN_URL}/thumbnails/${assetId}`,
              });
            }
            return memo;
          }, [])
        );
        setTabResultCounts(
          results.reduce<{
            [key: string]: number;
          }>((memo, result, idx) => {
            memo[tags[idx]] = result.nbHits;
            return memo;
          }, {})
        );
      } catch (error) {
        errorToast({
          title: "Error fetching explore results",
          description: "Please try again later",
        });
      }
    };
    void search();
  }, [
    tags,
    searchWithTokenRenewal,
    publicIndex,
    errorToast,
    imageTypesQueryArgs.types,
  ]);

  // ----------------------------------

  return useMemo(
    () => ({
      tabs: tabsWithImg,
      tabsStatus,
      markAsUpToDate,
      isSuggestsAvailable,
    }),
    [tabsWithImg, tabsStatus, markAsUpToDate, isSuggestsAvailable]
  );
}
